import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  Grid,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Button,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FolderIcon from '@mui/icons-material/Folder';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ProjectTile from '../components/ProjectTile';
import EstimateTile from '../components/EstimateTile';
import Navigation from '../components/Navigation';
import RoleSwitcherBar from '../components/RoleSwitcherBar';
import SearchBar from '../components/SearchBar';
import FilterPanel from '../components/FilterPanel';
import useDashboardData from '../hooks/useDashboardData';

const HomeownerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    projects, estimates, notifications, unreadCount,
    loading, error, setError,
    searchQuery, setSearchQuery,
    projectFilters, setProjectFilters,
    fetchProjects, fetchEstimates,
    notificationsAnchor,
    handleNotificationsClick,
    handleNotificationsClose,
    handleMarkAsRead,
  } = useDashboardData();

  const pendingEstimates = estimates.filter(est => est.status === 'pending_approval');
  const activeProjects = projects.filter((p) => p.status === 'active');

  const totalProjectValue = projects.reduce((sum, p) => {
    // ?? not || — a project legitimately at $0 should not fall through
    // to total_budget.
    const raw = p.budget ?? p.total_budget ?? 0;
    const b = Number(raw);
    return Number.isFinite(b) ? sum + b : sum;
  }, 0);

  const fmtCurrency = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const kpis = [
    {
      label: 'Active Projects',
      value: activeProjects.length,
      caption: activeProjects.length === 1 ? '1 in progress' : `${activeProjects.length} in progress`,
      color: '#1976d2',
      icon: <FolderIcon sx={{ color: '#1976d2' }} />,
    },
    {
      label: 'Awaiting Your Approval',
      value: pendingEstimates.length,
      caption: pendingEstimates.length === 0 ? 'All caught up' : 'Action needed',
      color: '#fb8c00',
      icon: <TaskAltIcon sx={{ color: '#fb8c00' }} />,
    },
    {
      label: 'Total Project Value',
      value: fmtCurrency(totalProjectValue),
      caption: `${projects.length} project${projects.length !== 1 ? 's' : ''} total`,
      color: '#43a047',
      icon: <AccountBalanceIcon sx={{ color: '#43a047' }} />,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Navigation
        onNotificationsClick={handleNotificationsClick}
        unreadCount={unreadCount}
        showNotifications={true}
        showLogout={true}
      />
      <RoleSwitcherBar />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>
            Welcome, {user?.first_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review estimates and approve your project items.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* KPI Row */}
        <Grid container spacing={2} mb={3}>
          {kpis.map((card) => (
            <Grid item xs={12} sm={4} key={card.label}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderTop: `4px solid ${card.color}`,
                  height: '100%',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {card.icon}
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {card.label}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: card.color, lineHeight: 1.15 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.caption}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Next-action banner */}
        {pendingEstimates.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              mb: 4,
              borderRadius: 2,
              borderLeft: '6px solid #fb8c00',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              backgroundColor: 'rgba(251, 140, 0, 0.08)',
            }}
          >
            <NotificationsActiveIcon sx={{ color: '#fb8c00', fontSize: 36 }} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography fontWeight={700}>
                {pendingEstimates.length === 1
                  ? '1 estimate is waiting for your approval'
                  : `${pendingEstimates.length} estimates are waiting for your approval`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review the line items and approve, or send it back with feedback.
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(`/estimate-review/${pendingEstimates[0].id}`)}
              sx={{ backgroundColor: '#fb8c00', '&:hover': { backgroundColor: '#fb8c00', opacity: 0.9 } }}
            >
              Review Estimate
            </Button>
          </Paper>
        )}

        {/* Pending Estimates Section */}
        {pendingEstimates.length > 0 && (
          <Box mb={6}>
            <Typography variant="h5" gutterBottom mb={3}>
              Needs Your Approval
            </Typography>
            <Grid container spacing={3}>
              {pendingEstimates.map((estimate) => (
                <Grid item xs={12} md={6} key={estimate.id}>
                  <EstimateTile estimate={estimate} onUpdate={fetchEstimates} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Projects Section */}
        <Typography variant="h5" gutterBottom mb={3}>
          Your Projects
        </Typography>
        <Box mb={3}>
          <SearchBar
            placeholder="Search projects..."
            onSearch={setSearchQuery}
            defaultValue={searchQuery}
          />
        </Box>
        <Box mb={3}>
          <FilterPanel
            filters={projectFilters}
            onFilterChange={setProjectFilters}
          />
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : projects.length === 0 && pendingEstimates.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              You're all set — your account is connected
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your contractor is preparing your estimate. You'll get a notification
              when it's ready for you to review and approve — nothing more to do right now.
            </Typography>
          </Box>
        ) : projects.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No active projects yet. Approve an estimate above to create a project.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} key={project.id}>
                <ProjectTile project={project} onUpdate={fetchProjects} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        PaperProps={{ sx: { width: 320, maxHeight: 400 } }}
      >
        {notifications.length === 0 ? (
          <MenuItem disabled>No notifications</MenuItem>
        ) : (
          notifications.slice(0, 10).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleMarkAsRead(notification.id)}
              sx={{
                whiteSpace: 'normal',
                backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
              }}
            >
              <Box>
                <Typography variant="body2">{notification.message}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(notification.created_at).toLocaleString()}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default HomeownerDashboard;
